import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Select from 'react-select';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getUserList } from '../../redux/selector';
import apiServices from '../../services/service';
import { Ic } from '../../shared/component/GosIcon';

/* GrowthOS — Team Management (design: growthos/TeamManagement.jsx).
   An overview (card / table grid + stats + search + view toggle + create drawer)
   and a per-team detail (hero + Members / Leads / Object-Assignment tabs), wired
   to the REAL team APIs (getTeams, getMembersByTeamId, addTeams, addMembersToTeam,
   removeTeamMember, getLeadsByTeam, addMappingToTeam, getTeamObjects,
   assignMemberToTeam). The design is a mock; fields with no backend (region,
   quota, attainment, description, edit/delete-team, team-role change) are dropped
   rather than fabricated. Card accent color is derived deterministically. */

const AVA_COLORS = ['#c96342', '#2e63a6', '#6a4fae', '#2c7a45', '#b34480', '#9a751c', '#4f6b8f', '#0e8c6f', '#2f7ea3', '#a35a2f'];
const hashIdx = (s, n) => {
  let h = 0;
  for (let i = 0; i < (s || '').length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h % n;
};
const colorFor = (s) => AVA_COLORS[hashIdx(String(s || ''), AVA_COLORS.length)];
const initialsOf = (n) =>
  (n || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase() || '?';

const Avatar = ({ name, color, size = 30 }) => (
  <span
    className="gos-tm-ava"
    style={{ width: size, height: size, background: color || colorFor(name), fontSize: Math.round(size * 0.36) }}
    title={name}
  >
    {initialsOf(name)}
  </span>
);

/* skeleton atoms (shared .gos-skel-an shimmer) */
const Skel = ({ w, h = 10, r = 5, style }) => (
  <span className="gos-skel-an" style={{ display: 'block', width: w, height: h, borderRadius: r, ...style }} />
);

const TeamCardSkeleton = () => (
  <div className="gos-tm-card" style={{ cursor: 'default' }}>
    <div className="gos-tm-card-accent gos-skel-an" />
    <div className="gos-tm-card-head">
      <span className="gos-skel-an" style={{ width: 40, height: 40, borderRadius: 11, flexShrink: 0 }} />
      <div style={{ flex: 1 }}><Skel w="60%" h={13} /></div>
    </div>
    <div className="gos-tm-leads">
      {[0, 1].map((i) => (
        <div className="gos-tm-lead" key={i}>
          <span className="gos-skel-an" style={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <Skel w="55%" h={9} />
            <Skel w="35%" h={8} style={{ marginTop: 6 }} />
          </div>
          <Skel w={44} h={16} r={6} />
        </div>
      ))}
    </div>
    <div className="gos-tm-foot">
      <Skel w={112} h={30} r={999} />
      <Skel w={56} h={9} />
    </div>
  </div>
);

const TableSkeleton = ({ cols, rows = 6 }) => (
  <div className="gos-tm-tblscroll">
    <table className="gos-tm-tbl">
      <tbody>
        {Array.from({ length: rows }).map((_, r) => (
          <tr key={r}>
            {Array.from({ length: cols }).map((_, c) => (
              <td key={c}>
                {c === 0 ? (
                  <div className="gos-tm-cellead">
                    <span className="gos-skel-an" style={{ width: 30, height: 30, borderRadius: '50%', flexShrink: 0 }} />
                    <Skel w={140} h={10} />
                  </div>
                ) : (
                  <Skel w={`${70 - c * 6}%`} h={10} />
                )}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const useOutside = (open, onClose) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const fn = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [open, onClose]);
  return ref;
};

const nameOf = (u) =>
  `${u?.firstName || ''} ${u?.lastName || ''}`.trim() || u?.userName || u?.emailAddress || u?.email || '';
const memberName = (m) =>
  `${m?.firstName || ''} ${m?.lastName || ''}`.trim() || m?.userName || m?.email || m?.memberId || '';
const emailOf = (m) => m?.email || m?.emailAddress || m?.userName || '';

const AvatarStack = ({ members, max = 5 }) => {
  const shown = members.slice(0, max);
  const extra = members.length - shown.length;
  return (
    <div className="gos-tm-stack">
      {shown.map((m, i) => (
        <span key={m.memberId || i} className="gos-tm-stack-av" style={{ zIndex: shown.length - i, marginLeft: i ? -9 : 0 }}>
          <Avatar name={memberName(m)} size={30} />
        </span>
      ))}
      {extra > 0 ? <span className="gos-tm-stack-more" style={{ marginLeft: -9 }}>{`+${extra}`}</span> : null}
    </div>
  );
};

/* react-select style to match the design's inputs (used in Add Members / Assign Leads) */
const gosSelect = {
  control: (base, s) => ({
    ...base, minHeight: 40, borderRadius: 10, fontSize: 13.5,
    borderColor: s.isFocused ? 'var(--gos-accent)' : 'var(--gos-border-strong)',
    boxShadow: s.isFocused ? '0 0 0 3px var(--gos-accent-soft)' : 'none',
    backgroundColor: 'var(--gos-surface)', '&:hover': { borderColor: 'var(--gos-accent)' },
  }),
  menu: (b) => ({ ...b, zIndex: 9999, backgroundColor: 'var(--gos-surface)', border: '1px solid var(--gos-border-strong)', borderRadius: 11, overflow: 'hidden' }),
  menuPortal: (b) => ({ ...b, zIndex: 9999 }),
  option: (b, s) => ({ ...b, fontSize: 13.5, backgroundColor: s.isSelected ? 'var(--gos-accent-soft)' : s.isFocused ? 'var(--gos-surface-3)' : 'transparent', color: s.isSelected ? 'var(--gos-accent-ink)' : 'var(--gos-text)' }),
  placeholder: (b) => ({ ...b, color: 'var(--gos-text-3)' }),
  singleValue: (b) => ({ ...b, color: 'var(--gos-text)' }),
  input: (b) => ({ ...b, color: 'var(--gos-text)' }),
  multiValue: (b) => ({ ...b, backgroundColor: 'var(--gos-accent-soft)', borderRadius: 6 }),
  multiValueLabel: (b) => ({ ...b, fontSize: 12, color: 'var(--gos-accent-ink)' }),
  indicatorSeparator: (b) => ({ ...b, backgroundColor: 'var(--gos-border)' }),
  dropdownIndicator: (b) => ({ ...b, color: 'var(--gos-text-3)' }),
};

/* create-team drawer — real addTeams (name + owner + captain); optional members
   added via addMembersToTeam after create. No edit (no update API). */
function TeamDrawer({ open, userOptions, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [owner, setOwner] = useState(null);
  const [captain, setCaptain] = useState(null);
  const [members, setMembers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState({});

  useEffect(() => {
    if (open) { setName(''); setOwner(null); setCaptain(null); setMembers([]); setErr({}); }
  }, [open]);

  if (!open) return null;

  const submit = () => {
    const e = {};
    if (!name.trim()) e.name = true;
    if (!owner) e.owner = true;
    if (!captain) e.captain = true;
    if (Object.keys(e).length) { setErr(e); return; }
    setSaving(true);
    apiServices
      .addTeams({ teamName: name.trim(), teamOwnerId: owner.value, teamCaptainId: captain.value })
      .then(async (res) => {
        const newId = res?.data?.id || res?.data?.team?.id;
        if (newId && members.length) {
          const ids = [...new Set([owner.value, captain.value, ...members.map((m) => m.value)])];
          await apiServices.addMembersToTeam({ teamId: newId, userIds: ids }).catch(() => {});
        }
        toast.success('Team created successfully!', { position: 'top-right' });
        onCreated();
      })
      .catch((error) =>
        toast.error(error?.response?.data?.message || error.message || 'Failed to create team', { position: 'top-right' })
      )
      .finally(() => setSaving(false));
  };

  return (
    <div className="gos-tm-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="gos-tm-drawer" onMouseDown={(e) => e.stopPropagation()}>
        <div className="gos-tm-drawer-head">
          <div>
            <div className="gos-tm-modal-title">New team</div>
            <div className="gos-tm-modal-sub">Name the team, set an owner and captain, and add members.</div>
          </div>
          <button type="button" className="gos-tm-icon-btn" onClick={onClose}><Ic name="x" size={18} /></button>
        </div>
        <div className="gos-tm-drawer-body">
          <label className="gos-tm-flabel">Team name<span className="gos-tm-req"> *</span></label>
          <input
            className={'gos-tm-input' + (err.name ? ' err' : '')}
            placeholder="e.g. West Region Sales"
            value={name}
            onChange={(e) => { setName(e.target.value); if (err.name) setErr((p) => ({ ...p, name: false })); }}
          />
          <label className="gos-tm-flabel" style={{ marginTop: 16 }}>Team owner<span className="gos-tm-req"> *</span></label>
          <Select
            options={userOptions} value={owner}
            onChange={(o) => { setOwner(o); if (err.owner) setErr((p) => ({ ...p, owner: false })); }}
            placeholder="Select team owner" menuPortalTarget={document.body} styles={gosSelect}
          />
          <label className="gos-tm-flabel" style={{ marginTop: 16 }}>Team captain<span className="gos-tm-req"> *</span></label>
          <Select
            options={userOptions} value={captain}
            onChange={(o) => { setCaptain(o); if (err.captain) setErr((p) => ({ ...p, captain: false })); }}
            placeholder="Select team captain" menuPortalTarget={document.body} styles={gosSelect}
          />
          <label className="gos-tm-flabel" style={{ marginTop: 16 }}>
            Members <span className="gos-tm-faint">({members.length} selected)</span>
          </label>
          <Select
            isMulti options={userOptions} value={members} onChange={setMembers}
            placeholder="Search and select members…" closeMenuOnSelect={false} hideSelectedOptions={false}
            menuPortalTarget={document.body} styles={gosSelect}
          />
        </div>
        <div className="gos-tm-modal-foot">
          <span />
          <div className="gos-tm-row" style={{ gap: 10 }}>
            <button type="button" className="gos-btn gos-btn-ghost" onClick={onClose}>Cancel</button>
            <button type="button" className="gos-btn gos-btn-primary" disabled={saving} onClick={submit}>
              <Ic name="check" size={16} />{saving ? 'Creating…' : 'Create team'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* inline member picker for the detail Members tab (design's tm-add-input dropdown) */
function AddMemberSelect({ candidates, onAdd }) {
  const [open, setOpen] = useState(false);
  const ref = useOutside(open, () => setOpen(false));
  return (
    <div className="gos-tm-add-select" ref={ref}>
      <button type="button" className={'gos-tm-add-input' + (open ? ' open' : '')} onClick={() => setOpen((o) => !o)}>
        <Ic name="search" size={15} />
        <span className="gos-tm-faint">Search and select members…</span>
        <Ic name="chevD" size={15} style={{ marginLeft: 'auto' }} />
      </button>
      {open ? (
        <div className="gos-tm-dd" style={{ top: 44, left: 0, right: 0 }}>
          {candidates.length ? (
            candidates.map((u) => (
              <button key={u.value} type="button" className="gos-tm-dd-item" onClick={() => { onAdd(u); setOpen(false); }}>
                <Avatar name={u.label} size={22} /> {u.label}
              </button>
            ))
          ) : (
            <div className="gos-tm-dd-empty">Everyone&apos;s already on this team.</div>
          )}
        </div>
      ) : null}
    </div>
  );
}

/* assigned-member picker for the Object Assignment tab (design's AssignSelect) */
function AssignSelect({ value, options, onChange }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const [pos, setPos] = useState(null);
  const current = options.find((o) => o.value === value);

  const place = () => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({ top: r.bottom + 4, left: r.left, width: r.width, maxH: Math.max(160, window.innerHeight - r.bottom - 12) });
  };
  useLayoutEffect(() => { if (open) place(); else setPos(null); }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    const reposition = () => place();
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    const onDoc = (e) => {
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
      document.removeEventListener('mousedown', onDoc);
    };
  }, [open]);

  return (
    <div className="gos-tm-assign">
      <button ref={btnRef} type="button" className={'gos-tm-assign-btn' + (open ? ' open' : '')} onClick={() => setOpen((o) => !o)}>
        {current ? (
          <span className="gos-tm-row" style={{ gap: 8, minWidth: 0 }}>
            <Avatar name={current.label} size={20} /><span className="gos-tm-assign-name">{current.label}</span>
          </span>
        ) : (
          <span className="gos-tm-assign-ph">Select member…</span>
        )}
        <span className="gos-tm-assign-tools">
          {current ? <span className="gos-tm-assign-x" onClick={(e) => { e.stopPropagation(); onChange(null); }}><Ic name="x" size={14} /></span> : null}
          <Ic name="chevD" size={15} />
        </span>
      </button>
      {open && pos
        ? createPortal(
            <div className="gos-tm">
              <div
                ref={menuRef}
                className="gos-tm-dd"
                style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, maxHeight: pos.maxH, overflowY: 'auto', zIndex: 9999 }}
              >
                {options.map((o) => (
                  <button key={o.value} type="button" className="gos-tm-dd-item" onClick={() => { onChange(o.value); setOpen(false); }}>
                    <Avatar name={o.label} size={22} /><span style={{ flex: 1 }}>{o.label}</span>
                    {o.value === value ? <Ic name="check" size={14} style={{ color: 'var(--gos-accent)' }} /> : null}
                  </button>
                ))}
                {options.length === 0 ? <div className="gos-tm-dd-empty">No members to assign.</div> : null}
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}

function TeamManagement({ embedded = false }) {
  const usersList = useSelector(getUserList);
  const Wrap = ({ children }) => (embedded ? <>{children}</> : <div className="inner-page">{children}</div>);
  const userOptions = useMemo(() => (usersList || []).map((u) => ({ value: u.userId, label: nameOf(u) })), [usersList]);
  const userName = useMemo(() => {
    const m = {};
    (usersList || []).forEach((u) => { m[u.userId] = nameOf(u); });
    return m;
  }, [usersList]);

  const [teams, setTeams] = useState([]); // enriched: {id, teamName, teamOwnerId, teamCaptainId, members:[]}
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [view, setView] = useState('grid');
  const [openId, setOpenId] = useState(null);
  const [drawer, setDrawer] = useState(false);

  const loadTeams = () => {
    setLoading(true);
    apiServices
      .getTeams()
      .then((res) => {
        const raw = res.data;
        const list = Array.isArray(raw) ? raw : raw?.teamList || raw?.teams || raw?.data || [];
        return Promise.all(
          list.map((t) =>
            apiServices
              .getMembersByTeamId(t.id)
              .then((r) => {
                const team = r.data.team || {};
                return {
                  id: t.id,
                  teamName: t.teamName,
                  teamOwnerId: team.teamOwnerId ?? t.teamOwnerId ?? null,
                  teamCaptainId: team.teamCaptainId ?? t.teamCaptainId ?? null,
                  members: team.memberDetails || [],
                };
              })
              .catch(() => ({ id: t.id, teamName: t.teamName, teamOwnerId: t.teamOwnerId ?? null, teamCaptainId: t.teamCaptainId ?? null, members: [] }))
          )
        );
      })
      .then((enriched) => setTeams(enriched))
      .catch((e) => console.error('getTeams', e))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadTeams(); /* eslint-disable-next-line */ }, []);

  const stats = useMemo(() => {
    const people = new Set();
    teams.forEach((t) => t.members.forEach((m) => people.add(m.memberId)));
    const totalMembers = teams.reduce((n, t) => n + t.members.length, 0);
    const avg = teams.length ? Math.round(totalMembers / teams.length) : 0;
    const withOwner = teams.filter((t) => t.teamOwnerId).length;
    return { teams: teams.length, people: people.size, avg, withOwner };
  }, [teams]);

  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return teams;
    return teams.filter((t) =>
      `${t.teamName} ${userName[t.teamOwnerId] || ''} ${userName[t.teamCaptainId] || ''}`.toLowerCase().includes(s)
    );
  }, [teams, q, userName]);

  const openTeam = teams.find((t) => t.id === openId) || null;

  if (openTeam) {
    return (
      <Wrap>
        <div className="gos-tm">
          <TeamDetail
            team={openTeam}
            userOptions={userOptions}
            userName={userName}
            onBack={() => setOpenId(null)}
            onMembersChanged={loadTeams}
          />
        </div>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <div className="gos-tm">
        <div className="gos-tm-head">
          <div>
            <div className="gos-tm-title">Team Management</div>
            <div className="gos-tm-sub">Organize reps into teams and assign an owner, captain, leads, and objects.</div>
          </div>
          <button type="button" className="gos-btn gos-btn-primary" onClick={() => setDrawer(true)}>
            <Ic name="plus" size={16} />New Team
          </button>
        </div>

        <div className="gos-tm-stats">
          {[
            ['Teams', stats.teams, 'building', '#6a4fae'],
            ['People assigned', stats.people, 'leads', 'var(--gos-text-3)'],
            ['Avg. team size', stats.avg, 'users', '#2c7a45'],
            ['Teams with owner', stats.withOwner, 'star', '#c96342'],
          ].map(([label, val, icon, col]) => (
            <div key={label} className="gos-tm-stat">
              <span className="gos-tm-stat-ic" style={{ color: col }}><Ic name={icon} size={17} /></span>
              <div>
                <div className="gos-tm-stat-val">{val}</div>
                <div className="gos-tm-stat-label">{label}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="gos-tm-toolbar">
          <div className="gos-tm-search">
            <Ic name="search" size={16} />
            <input placeholder="Search teams…" value={q} onChange={(e) => setQ(e.target.value)} />
            {q ? <button type="button" className="gos-tm-clear" onClick={() => setQ('')}><Ic name="x" size={14} /></button> : null}
          </div>
          <span className="gos-tm-count"><strong>{rows.length}</strong> teams</span>
          <div className="gos-tm-grow" />
          <div className="gos-tm-viewtoggle">
            <button type="button" className={view === 'grid' ? 'on' : ''} onClick={() => setView('grid')}><Ic name="grid" size={15} />Grid</button>
            <button type="button" className={view === 'table' ? 'on' : ''} onClick={() => setView('table')}><Ic name="list" size={16} />Table</button>
          </div>
        </div>

        {loading ? (
          <div className="gos-tm-grid">
            {Array.from({ length: 6 }).map((_, i) => <TeamCardSkeleton key={i} />)}
          </div>
        ) : rows.length === 0 ? (
          <div className="gos-tm-cardbox"><div className="gos-tm-empty">{teams.length ? 'No teams match your search.' : 'No teams yet — click New Team.'}</div></div>
        ) : view === 'table' ? (
          <div className="gos-tm-cardbox" style={{ overflow: 'hidden', padding: 0 }}>
            <div className="gos-tm-tblscroll">
              <table className="gos-tm-tbl">
                <thead>
                  <tr><th>Team</th><th>Owner</th><th>Captain</th><th style={{ textAlign: 'center' }}>Members</th></tr>
                </thead>
                <tbody>
                  {rows.map((t) => (
                    <tr key={t.id} className="gos-tm-trow" onClick={() => setOpenId(t.id)}>
                      <td>
                        <div className="gos-tm-row" style={{ gap: 10 }}>
                          <span className="gos-tm-dot" style={{ background: colorFor(t.id) }} />
                          <span className="gos-tm-cellname">{t.teamName}</span>
                        </div>
                      </td>
                      <td>{t.teamOwnerId ? <div className="gos-tm-row" style={{ gap: 8 }}><Avatar name={userName[t.teamOwnerId]} size={24} /><span className="gos-tm-cell">{userName[t.teamOwnerId] || '—'}</span></div> : <span className="gos-tm-muted">—</span>}</td>
                      <td>{t.teamCaptainId ? <div className="gos-tm-row" style={{ gap: 8 }}><Avatar name={userName[t.teamCaptainId]} size={24} /><span className="gos-tm-cell">{userName[t.teamCaptainId] || '—'}</span></div> : <span className="gos-tm-muted">—</span>}</td>
                      <td style={{ textAlign: 'center' }}><span className="gos-tm-muted">{t.members.length}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="gos-tm-grid">
            {rows.map((t) => (
              <div key={t.id} className="gos-tm-card" onClick={() => setOpenId(t.id)}>
                <div className="gos-tm-card-accent" style={{ background: colorFor(t.id) }} />
                <div className="gos-tm-card-head">
                  <span className="gos-tm-card-badge" style={{ background: `${colorFor(t.id)}1f`, color: colorFor(t.id) }}><Ic name="building" size={18} /></span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="gos-tm-card-name">{t.teamName}</div>
                  </div>
                </div>
                <div className="gos-tm-leads">
                  <div className="gos-tm-lead">
                    <Avatar name={userName[t.teamOwnerId]} size={28} />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div className="gos-tm-lead-name">{userName[t.teamOwnerId] || 'Unassigned'}</div>
                      <div className="gos-tm-lead-role"><Ic name="star" size={10} />Owner</div>
                    </div>
                    <span className="gos-tm-role-tag owner">Owner</span>
                  </div>
                  <div className="gos-tm-lead">
                    <Avatar name={userName[t.teamCaptainId]} size={28} />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div className="gos-tm-lead-name">{userName[t.teamCaptainId] || 'Unassigned'}</div>
                      <div className="gos-tm-lead-role"><Ic name="shield" size={10} />Captain</div>
                    </div>
                    <span className="gos-tm-role-tag captain">Captain</span>
                  </div>
                </div>
                <div className="gos-tm-foot">
                  {t.members.length ? <AvatarStack members={t.members} max={5} /> : <span className="gos-tm-muted">No members</span>}
                  <span className="gos-tm-count-sm">{t.members.length} member{t.members.length !== 1 ? 's' : ''}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <TeamDrawer
        open={drawer}
        userOptions={userOptions}
        onClose={() => setDrawer(false)}
        onCreated={() => { setDrawer(false); loadTeams(); }}
      />
    </Wrap>
  );
}

/* ── per-team detail: hero + Members / Leads / Object-Assignment tabs ── */
function TeamDetail({ team, userOptions, userName, onBack, onMembersChanged }) {
  const [tab, setTab] = useState('members');

  // members
  const [members, setMembers] = useState(team.members || []);
  const [ownerId, setOwnerId] = useState(team.teamOwnerId);
  const [captainId, setCaptainId] = useState(team.teamCaptainId);
  const [membersToAdd, setMembersToAdd] = useState([]);
  const [savingMembers, setSavingMembers] = useState(false);

  // leads
  const [allLeads, setAllLeads] = useState([]);
  const [teamLeads, setTeamLeads] = useState([]);
  const [teamLeadsLoading, setTeamLeadsLoading] = useState(false);
  const [leadsToAssign, setLeadsToAssign] = useState([]);
  const [savingLeads, setSavingLeads] = useState(false);

  // objects
  const [teamObjects, setTeamObjects] = useState([]);
  const [objectsLoading, setObjectsLoading] = useState(false);
  const [assignMap, setAssignMap] = useState({});

  const refetchMembers = () => {
    apiServices.getMembersByTeamId(team.id).then((r) => {
      const t = r.data.team || {};
      setMembers(t.memberDetails || []);
      if (t.teamOwnerId) setOwnerId(t.teamOwnerId);
      if (t.teamCaptainId) setCaptainId(t.teamCaptainId);
    }).catch((e) => console.error('getMembersByTeamId', e));
  };

  useEffect(() => { refetchMembers(); /* eslint-disable-next-line */ }, [team.id]);

  // leads: all (for assign) + team's assigned
  useEffect(() => {
    apiServices
      .getLeadList({ pageSize: 500, pageNumber: 1, columnName: 'UpdatedDateTime', orderType: 'desc', filterJson: null })
      .then((r) => setAllLeads(r.data.leads || []))
      .catch((e) => console.error('getLeadList', e));
  }, []);

  const loadTeamLeads = () => {
    setTeamLeadsLoading(true);
    apiServices
      .getLeadsByTeam(team.id)
      .then((r) => {
        const raw = r.data;
        setTeamLeads(Array.isArray(raw) ? raw : raw?.leads || raw?.data || []);
      })
      .catch((e) => console.error('getLeadsByTeam', e))
      .finally(() => setTeamLeadsLoading(false));
  };
  useEffect(() => { loadTeamLeads(); /* eslint-disable-next-line */ }, [team.id]);

  useEffect(() => {
    if (tab !== 'objects') return;
    setObjectsLoading(true);
    apiServices
      .getTeamObjects(team.id)
      .then((r) => {
        const raw = r.data;
        const data = Array.isArray(raw) ? raw : raw?.teamObjects || raw?.data || [];
        setTeamObjects(data);
        const m = {};
        data.forEach((o) => { if (o.assignedToTeamMemberId) m[o.id] = o.assignedToTeamMemberId; });
        setAssignMap(m);
      })
      .catch((e) => console.error('getTeamObjects', e))
      .finally(() => setObjectsLoading(false));
  }, [tab, team.id]);

  const roleOf = (m) =>
    m.memberId === ownerId ? 'owner' : m.memberId === captainId ? 'captain' : 'member';

  const candidateOptions = useMemo(() => {
    const has = new Set(members.map((m) => m.memberId));
    return userOptions.filter((u) => !has.has(u.value));
  }, [userOptions, members]);

  const saveMembers = () => {
    if (!membersToAdd.length) return;
    setSavingMembers(true);
    const ids = [...new Set([...members.map((m) => m.memberId), ...membersToAdd.map((m) => m.value)])];
    apiServices
      .addMembersToTeam({ teamId: team.id, userIds: ids })
      .then(() => {
        toast.success('Members added successfully!', { position: 'top-right' });
        setMembersToAdd([]);
        refetchMembers();
        onMembersChanged && onMembersChanged();
      })
      .catch((error) => toast.error(error?.response?.data?.message || error.message || 'Failed to add members', { position: 'top-right' }))
      .finally(() => setSavingMembers(false));
  };

  const removeMember = (m) => {
    if (m.memberId === ownerId || m.memberId === captainId) {
      toast.info('Reassign the owner/captain before removing this member.', { position: 'top-right' });
      return;
    }
    apiServices
      .removeTeamMember({ teamId: team.id, memberId: m.memberId })
      .then(() => {
        toast.success('Member removed successfully!', { position: 'top-right' });
        setMembers((prev) => prev.filter((x) => x.memberId !== m.memberId));
        onMembersChanged && onMembersChanged();
      })
      .catch((error) => toast.error(error?.response?.data?.message || error.message || 'Failed to remove member', { position: 'top-right' }));
  };

  const leadOptions = useMemo(
    () => allLeads.map((l) => ({
      value: l.id,
      label: `${l.firstName || ''} ${l.lastName || ''}`.trim() || l.companyName || l.email || l.id,
      meta: { company: l.companyName, email: l.email },
    })),
    [allLeads]
  );
  const availableLeadOptions = useMemo(() => {
    const assigned = new Set(teamLeads.map((l) => l.objectId?.toLowerCase()));
    return leadOptions.filter((o) => !assigned.has(o.value?.toLowerCase()));
  }, [leadOptions, teamLeads]);

  const saveLeads = () => {
    if (!leadsToAssign.length) return;
    setSavingLeads(true);
    apiServices
      .addMappingToTeam({ teamId: team.id, objectType: 'Lead', assignedToTeamMemberId: null, objectIds: leadsToAssign.map((l) => l.value) })
      .then(() => {
        toast.success('Leads assigned successfully!', { position: 'top-right' });
        setLeadsToAssign([]);
        loadTeamLeads();
      })
      .catch((error) => toast.error(error?.response?.data?.message || error.message || 'Failed to assign leads', { position: 'top-right' }))
      .finally(() => setSavingLeads(false));
  };

  const memberOptions = useMemo(() => members.map((m) => ({ value: m.memberId, label: memberName(m) })), [members]);

  const assignObject = (objectId, memberId) => {
    setAssignMap((prev) => ({ ...prev, [objectId]: memberId }));
    apiServices
      .assignMemberToTeam({ id: objectId, assignedToTeamMemberId: memberId })
      .then(() => toast.success('Member assigned successfully!', { position: 'top-right' }))
      .catch((error) => toast.error(error?.response?.data?.message || error.message || 'Failed to assign member', { position: 'top-right' }));
  };

  const objTypeIcon = (t) => (t === 'Lead' ? 'leads' : t === 'Account' ? 'building' : t === 'Contact' ? 'contacts' : t === 'Campaign' ? 'campaigns' : 'note');

  return (
    <>
      <div className="gos-tm-detail-top">
        <button type="button" className="gos-tm-back" onClick={onBack}><Ic name="chevL" size={17} />Teams</button>
      </div>

      <div className="gos-tm-cardbox gos-tm-hero">
        <span className="gos-tm-card-badge" style={{ background: `${colorFor(team.id)}1f`, color: colorFor(team.id), width: 52, height: 52 }}><Ic name="building" size={22} /></span>
        <div className="gos-tm-hero-meta">
          <div className="gos-tm-hero-name">{team.teamName}</div>
          <div className="gos-tm-hero-sub"><Ic name="users" size={13} />{members.length} member{members.length !== 1 ? 's' : ''}</div>
        </div>
        <div className="gos-tm-hero-people">
          {ownerId ? <div className="gos-tm-hero-person"><Avatar name={userName[ownerId]} size={26} /><span>{userName[ownerId]}</span><span className="gos-tm-role-tag owner">Owner</span></div> : null}
          {captainId ? <div className="gos-tm-hero-person"><Avatar name={userName[captainId]} size={26} /><span>{userName[captainId]}</span><span className="gos-tm-role-tag captain">Captain</span></div> : null}
        </div>
      </div>

      <div className="gos-tm-tabs">
        <button type="button" className={'gos-tm-tab' + (tab === 'members' ? ' active' : '')} onClick={() => setTab('members')}>
          <Ic name="leads" size={15} />Members<span className="gos-tm-tabcount">{members.length}</span>
        </button>
        <button type="button" className={'gos-tm-tab' + (tab === 'leads' ? ' active' : '')} onClick={() => setTab('leads')}>
          <Ic name="target" size={15} />Leads<span className="gos-tm-tabcount">{teamLeads.length}</span>
        </button>
        <button type="button" className={'gos-tm-tab' + (tab === 'objects' ? ' active' : '')} onClick={() => setTab('objects')}>
          <Ic name="grid" size={15} />Object Assignment
        </button>
      </div>

      {tab === 'members' ? (
        <div className="gos-tm-cardbox" style={{ overflow: 'visible', padding: 0 }}>
          <div className="gos-tm-addbar">
            <div style={{ flex: 1 }}>
              <Select
                isMulti options={candidateOptions} value={membersToAdd} onChange={setMembersToAdd}
                placeholder="Search and select members…" closeMenuOnSelect={false} hideSelectedOptions={false}
                menuPortalTarget={document.body} styles={gosSelect}
              />
            </div>
            <button type="button" className="gos-btn gos-btn-primary" disabled={savingMembers || !membersToAdd.length} onClick={saveMembers}>
              {savingMembers ? 'Saving…' : 'Save Members'}
            </button>
          </div>
          <div className="gos-tm-tblscroll">
            <table className="gos-tm-tbl">
              <thead>
                <tr><th>Name</th><th>Email / Username</th><th>Team Role</th><th style={{ width: 64, textAlign: 'center' }}>Action</th></tr>
              </thead>
              <tbody>
                {members.map((m) => {
                  const role = roleOf(m);
                  return (
                    <tr key={m.memberId}>
                      <td><div className="gos-tm-cellead"><Avatar name={memberName(m)} size={30} /><span className="gos-tm-cellname">{memberName(m)}</span></div></td>
                      <td><span className="gos-tm-cell gos-tm-muted">{emailOf(m) || '—'}</span></td>
                      <td><span className={'gos-tm-role-tag ' + role}>{role === 'owner' ? 'Owner' : role === 'captain' ? 'Captain' : 'Member'}</span></td>
                      <td style={{ textAlign: 'center' }}>
                        <button type="button" className="gos-tm-iconact" title="Remove" onClick={() => removeMember(m)}><Ic name="trash" size={15} /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {members.length === 0 ? <div className="gos-tm-empty">No members yet. Add members above.</div> : null}
        </div>
      ) : null}

      {tab === 'leads' ? (
        <div className="gos-tm-cardbox" style={{ overflow: 'visible', padding: 0 }}>
          <div className="gos-tm-addbar">
            <div style={{ flex: 1 }}>
              <Select
                isMulti options={availableLeadOptions} value={leadsToAssign} onChange={setLeadsToAssign}
                placeholder="Search and select leads…" closeMenuOnSelect={false} hideSelectedOptions={false}
                menuPortalTarget={document.body} styles={gosSelect}
                formatOptionLabel={(opt) => (
                  <div>
                    <div style={{ fontWeight: 500 }}>{opt.label}</div>
                    {(opt.meta?.company || opt.meta?.email) ? (
                      <div style={{ color: 'var(--gos-text-3)', fontSize: '.73rem' }}>
                        {[opt.meta.company, opt.meta.email].filter(Boolean).join(' · ')}
                      </div>
                    ) : null}
                  </div>
                )}
              />
            </div>
            <button type="button" className="gos-btn gos-btn-primary" disabled={savingLeads || !leadsToAssign.length} onClick={saveLeads}>
              {savingLeads ? 'Saving…' : 'Save Leads'}
            </button>
          </div>
          {teamLeadsLoading ? (
            <TableSkeleton cols={6} />
          ) : (
            <>
              <div className="gos-tm-tblscroll">
                <table className="gos-tm-tbl">
                  <thead>
                    <tr><th>Name</th><th>Company</th><th>Object Type</th><th>Email</th><th>Phone</th><th>Owner</th></tr>
                  </thead>
                  <tbody>
                    {teamLeads.map((l, i) => (
                      <tr key={l.objectId || i}>
                        <td><span className="gos-tm-cellname">{l.fullName || `${l.firstName || ''} ${l.lastName || ''}`.trim() || '—'}</span></td>
                        <td><span className="gos-tm-cell gos-tm-muted">{l.companyName || '—'}</span></td>
                        <td><span className="gos-tm-type">{l.objectType || 'Lead'}</span></td>
                        <td><span className="gos-tm-cell gos-tm-muted">{l.email || '—'}</span></td>
                        <td><span className="gos-tm-mono">{l.phone || l.directPhone || l.mobile || '—'}</span></td>
                        <td>{l.owner ? <div className="gos-tm-row" style={{ gap: 8 }}><Avatar name={l.owner} size={22} /><span className="gos-tm-cell gos-tm-muted">{l.owner}</span></div> : <span className="gos-tm-muted">—</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {teamLeads.length === 0 ? <div className="gos-tm-empty">No leads assigned to this team yet.</div> : null}
            </>
          )}
        </div>
      ) : null}

      {tab === 'objects' ? (
        <div className="gos-tm-cardbox" style={{ overflow: 'visible', padding: 0 }}>
          {objectsLoading ? (
            <TableSkeleton cols={4} />
          ) : (
            <>
              <div className="gos-tm-tblscroll">
                <table className="gos-tm-tbl gos-tm-objtbl">
                  <thead>
                    <tr><th>Object ID</th><th>Object Type</th><th>Object Name</th><th style={{ width: 280 }}>Assigned To</th></tr>
                  </thead>
                  <tbody>
                    {teamObjects.map((o) => (
                      <tr key={o.id}>
                        <td><span className="gos-tm-mono gos-tm-objid" title={o.objectId}>{o.objectId}</span></td>
                        <td><span className="gos-tm-modchip"><Ic name={objTypeIcon(o.objectType)} size={12} />{o.objectType}</span></td>
                        <td><span className="gos-tm-cellname">{o.objectName || '—'}</span></td>
                        <td><AssignSelect value={assignMap[o.id] ?? o.assignedToTeamMemberId ?? null} options={memberOptions} onChange={(v) => assignObject(o.id, v)} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {teamObjects.length === 0 ? <div className="gos-tm-empty">No objects assigned to this team yet.</div> : null}
            </>
          )}
        </div>
      ) : null}
    </>
  );
}

export default TeamManagement;
